import { successProbability } from "./dice.js";

export type RunDifficultyLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export interface RunDifficultySettings {
  level: RunDifficultyLevel;
  label: string;
  scoreMultiplier: number;
  checkProbabilityShift: number;
  minCheckProbability: number;
  maxCheckProbability: number;
  maxRequiredPointShift: number;
  startingLuckDelta: number;
  positiveItemWeight: number;
  negativeItemWeight: number;
  luckyItemWeight: number;
  openingPenaltyItems: number;
  openingMarkerItems: string[];
  maxRerollsPerCheck: number | null;
  rules: string[];
}

export const DEFAULT_RUN_DIFFICULTY: RunDifficultyLevel = 3;
export const RUN_DIFFICULTY_LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export const RUN_DIFFICULTY_SETTINGS: Record<
  RunDifficultyLevel,
  RunDifficultySettings
> = {
  0: {
    level: 0,
    label: "观光",
    scoreMultiplier: 0.95,
    checkProbabilityShift: 0.07,
    minCheckProbability: 0.42,
    maxCheckProbability: 0.94,
    maxRequiredPointShift: 6,
    startingLuckDelta: 1,
    positiveItemWeight: 1.1,
    negativeItemWeight: 0.92,
    luckyItemWeight: 1.12,
    openingPenaltyItems: 0,
    openingMarkerItems: ["19710"],
    maxRerollsPerCheck: null,
    rules: ["初始气运 +1", "解锁灯火小委托", "检定宽松但保留失败风险"],
  },
  1: {
    level: 1,
    label: "从容",
    scoreMultiplier: 1.08,
    checkProbabilityShift: 0.07,
    minCheckProbability: 0.36,
    maxCheckProbability: 0.91,
    maxRequiredPointShift: 6,
    startingLuckDelta: 1,
    positiveItemWeight: 1.07,
    negativeItemWeight: 0.96,
    luckyItemWeight: 1.08,
    openingPenaltyItems: 0,
    openingMarkerItems: [],
    maxRerollsPerCheck: null,
    rules: ["初始气运 +1", "检定略微宽松", "负面藏品略少"],
  },
  2: {
    level: 2,
    label: "平稳",
    scoreMultiplier: 1.22,
    checkProbabilityShift: 0.04,
    minCheckProbability: 0.3,
    maxCheckProbability: 0.88,
    maxRequiredPointShift: 5,
    startingLuckDelta: 1,
    positiveItemWeight: 1.03,
    negativeItemWeight: 0.99,
    luckyItemWeight: 1.04,
    openingPenaltyItems: 0,
    openingMarkerItems: [],
    maxRerollsPerCheck: null,
    rules: ["初始气运 +1", "检定小幅宽松", "接近标准藏品权重"],
  },
  3: {
    level: 3,
    label: "标准",
    scoreMultiplier: 1.4,
    checkProbabilityShift: 0,
    minCheckProbability: 0.24,
    maxCheckProbability: 0.84,
    maxRequiredPointShift: 5,
    startingLuckDelta: 0,
    positiveItemWeight: 1,
    negativeItemWeight: 1,
    luckyItemWeight: 1,
    openingPenaltyItems: 0,
    openingMarkerItems: [],
    maxRerollsPerCheck: null,
    rules: ["标准检定曲线", "标准气运", "标准藏品权重"],
  },
  4: {
    level: 4,
    label: "险境",
    scoreMultiplier: 1.62,
    checkProbabilityShift: -0.06,
    minCheckProbability: 0.18,
    maxCheckProbability: 0.78,
    maxRequiredPointShift: 6,
    startingLuckDelta: -1,
    positiveItemWeight: 0.96,
    negativeItemWeight: 1.12,
    luckyItemWeight: 0.95,
    openingPenaltyItems: 0,
    openingMarkerItems: [],
    maxRerollsPerCheck: null,
    rules: ["初始气运 -1", "检定略微收紧", "负面藏品更常见"],
  },
  5: {
    level: 5,
    label: "绝境",
    scoreMultiplier: 1.9,
    checkProbabilityShift: -0.11,
    minCheckProbability: 0.14,
    maxCheckProbability: 0.72,
    maxRequiredPointShift: 7,
    startingLuckDelta: -1,
    positiveItemWeight: 0.9,
    negativeItemWeight: 1.3,
    luckyItemWeight: 0.88,
    openingPenaltyItems: 1,
    openingMarkerItems: [],
    maxRerollsPerCheck: null,
    rules: ["初始气运 -1", "开局携带 1 件负面藏品", "检定明显收紧"],
  },
  6: {
    level: 6,
    label: "天命",
    scoreMultiplier: 2.2,
    checkProbabilityShift: -0.16,
    minCheckProbability: 0.14,
    maxCheckProbability: 0.64,
    maxRequiredPointShift: 8,
    startingLuckDelta: -2,
    positiveItemWeight: 0.8,
    negativeItemWeight: 1.7,
    luckyItemWeight: 0.8,
    openingPenaltyItems: 2,
    openingMarkerItems: [],
    maxRerollsPerCheck: null,
    rules: ["初始气运降至 0", "开局携带 2 件负面藏品", "检定压迫最强但保留生路"],
  },
  7: {
    level: 7,
    label: "裂隙",
    scoreMultiplier: 2.6,
    checkProbabilityShift: -0.19,
    minCheckProbability: 0.14,
    maxCheckProbability: 0.58,
    maxRequiredPointShift: 8,
    startingLuckDelta: -2,
    positiveItemWeight: 0.76,
    negativeItemWeight: 1.95,
    luckyItemWeight: 0.72,
    openingPenaltyItems: 2,
    openingMarkerItems: ["19720"],
    maxRerollsPerCheck: null,
    rules: ["初始气运降至 0", "开局携带 2 件负面藏品", "解锁裂命赌局"],
  },
  8: {
    level: 8,
    label: "无归",
    scoreMultiplier: 3.1,
    checkProbabilityShift: -0.23,
    minCheckProbability: 0.14,
    maxCheckProbability: 0.52,
    maxRequiredPointShift: 9,
    startingLuckDelta: -2,
    positiveItemWeight: 0.73,
    negativeItemWeight: 2.18,
    luckyItemWeight: 0.66,
    openingPenaltyItems: 3,
    openingMarkerItems: ["19720", "19723"],
    maxRerollsPerCheck: null,
    rules: ["初始气运降至 0", "开局携带 3 件负面藏品", "解锁无归钟试炼"],
  },
  9: {
    level: 9,
    label: "逆命",
    scoreMultiplier: 3.65,
    checkProbabilityShift: -0.24,
    minCheckProbability: 0.14,
    maxCheckProbability: 0.5,
    maxRequiredPointShift: 10,
    startingLuckDelta: -2,
    positiveItemWeight: 0.7,
    negativeItemWeight: 2.38,
    luckyItemWeight: 0.58,
    openingPenaltyItems: 3,
    openingMarkerItems: ["19720", "19723", "19726"],
    maxRerollsPerCheck: null,
    rules: ["初始气运降至 0", "开局携带 3 件负面藏品", "无归后解锁逆命烛试炼"],
  },
  10: {
    level: 10,
    label: "断界",
    scoreMultiplier: 4.25,
    checkProbabilityShift: -0.26,
    minCheckProbability: 0.14,
    maxCheckProbability: 0.46,
    maxRequiredPointShift: 11,
    startingLuckDelta: -2,
    positiveItemWeight: 0.66,
    negativeItemWeight: 2.7,
    luckyItemWeight: 0.52,
    openingPenaltyItems: 4,
    openingMarkerItems: ["19720", "19723", "19726", "19729"],
    maxRerollsPerCheck: 1,
    rules: ["初始气运降至 0", "开局携带 4 件负面藏品", "每次检定最多重掷 1 次", "逆命后解锁断界门试炼"],
  },
};

export function normalizeRunDifficulty(value: unknown): RunDifficultyLevel {
  const n = Math.trunc(Number(value));
  return RUN_DIFFICULTY_LEVELS.includes(n as RunDifficultyLevel)
    ? (n as RunDifficultyLevel)
    : DEFAULT_RUN_DIFFICULTY;
}

export function runDifficultySettings(
  level: unknown,
): RunDifficultySettings {
  return RUN_DIFFICULTY_SETTINGS[normalizeRunDifficulty(level)];
}

export function requiredPointsForRunDifficulty(
  baseRequiredPoints: number,
  currentPoints: number,
  level: unknown,
): number {
  const base = Math.trunc(Number(baseRequiredPoints) || 0);
  if (base <= 0) return 0;
  const current = Number(currentPoints) || 0;
  const settings = runDifficultySettings(level);
  const baseProbability = successProbability(current, base, 2);
  const targetProbability = clamp(
    baseProbability + settings.checkProbabilityShift,
    settings.minCheckProbability,
    settings.maxCheckProbability,
  );
  let bestReq = base;
  let bestDelta = Number.POSITIVE_INFINITY;
  // Search around both the authored DC and the player's reachable 2d6 band.
  const buildAwareLower = Math.floor(current) - 12;
  const buildAwareUpper = Math.ceil(current) + 12;
  const lower = Math.max(
    1,
    Math.min(base - settings.maxRequiredPointShift, buildAwareLower),
  );
  const upper = Math.min(
    60,
    Math.max(base + settings.maxRequiredPointShift, buildAwareUpper),
  );
  for (let req = lower; req <= upper; req++) {
    const delta = Math.abs(successProbability(current, req, 2) - targetProbability);
    if (delta < bestDelta) {
      bestReq = req;
      bestDelta = delta;
    }
  }
  return Math.round(bestReq);
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
